import { Frequency, RRule, RRuleSet, Weekday } from 'rrule';
import { format, parse, parseISO, startOfDay } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc, formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import { addYears, addHours } from 'date-fns';

/**
 * Recurrence is tricky: all recurrence dates must be generated in the context of their relevant timezone
 * (to offload correctly identifying days of the week/time as they interact with daylight savings etc to RRUle)
 * but we will need to save/utilize all dates as UTC.
 *
 */
export class Recurrence {
  // Base properties
  id: number;
  createdOn: Date;
  updatedOn: Date;
  deleted: boolean;

  // Rrule set properties
  exdates: Date[];

  // RRule properties
  freq: Frequency;
  dtstart: Date;
  interval: number;
  wkst: Weekday | number;
  count?: number;
  until?: Date | null;
  byweekday?: Weekday[] | null;
  tzid: string;

  // Combined with byweekday for targeting nth weekdays of the month
  nbyweekday?: number | null; // for monthly recurrence, numeric value for 'first/last/etc' weekday of the month

  // Calculated
  rruleSet?: RRuleSet;
  translatedUtcDates?: string[]; // Changed from DateTime[] to string[]
  formattedJsDates?: string[]; // DISPLAYED IN TIMEZONE: formatted for simple 'yyyy MM dd' format

  constructor(data: any) {
    // if ((!data.count && !data.until) || !data.interval) {
    //   // 'Recurrence requires either a set count or "until" date and a selected interval to prevent rrule generating effectively endless dates.'
    //   return;
    // }
    // if nbyweekday value, map week days with numeric reference
    if (data.byweekday?.length > 0) {
      if (typeof data.byweekday[0] === 'object') {
        data.nbyweekday = data.byweekday[0].n;
      } else if (data.nbyweekday) {
        data.byweekday = data.byweekday.map((day: string | number) => {
          let rruleDay: string | Weekday;

          if (isNaN(Number(day))) {
            // @ts-ignore
            rruleDay = RRule[day];
          } else {
            switch (Number(day)) {
              case RRule.MO.weekday:
                rruleDay = RRule.MO;
                break;
              case RRule.TU.weekday:
                rruleDay = RRule.TU;
                break;
              case RRule.WE.weekday:
                rruleDay = RRule.WE;
                break;
              case RRule.TH.weekday:
                rruleDay = RRule.TH;
                break;
              case RRule.FR.weekday:
                rruleDay = RRule.FR;
                break;
              case RRule.SA.weekday:
                rruleDay = RRule.SA;
                break;
              case RRule.SU.weekday:
                rruleDay = RRule.SU;
                break;
            }
          }

          return (rruleDay as Weekday).nth(data.nbyweekday);
        });
      } else if (
        typeof data.byweekday[0] === 'string' &&
        !isNaN(Number(data.byweekday[0]))
      ) {
        data.byweekday = data.byweekday = data.byweekday.map((day: string) => {
          return Number(day);
        });
      }
    }

    if (data.stopCondition) {
      delete data.stopCondition;
    }

    // Dates must be JS Dates for creating new rrule / rruleSets
    if (data.dtstart && !(data.dtstart instanceof Date)) {
      // If we have a tzid and a date-only string (yyyy-MM-dd), anchor it to midnight in that timezone
      if (
        typeof data.dtstart === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(data.dtstart) &&
        data.tzid
      ) {
        // Convert midnight at tzid to a UTC Date to keep RRule anchored correctly to the site day
        data.dtstart = zonedTimeToUtc(`${data.dtstart}T00:00:00`, data.tzid);
      } else {
        data.dtstart = parseISO(data.dtstart);
      }
    }

    if (data.until && !(data.until instanceof Date)) {
      // For date-only strings, interpret as end-of-day in tzid to include the full day in the rule
      if (
        typeof data.until === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(data.until) &&
        data.tzid
      ) {
        // Use 23:59:59 to keep inclusive behavior for that day
        data.until = zonedTimeToUtc(`${data.until}T23:59:59`, data.tzid);
      } else {
        data.until = parseISO(data.until);
      }
    }

    // date coming in as UTC iso string, convert to timezone-friendly, UTC JS Date
    if (data.exdates?.length > 0 && !(data.exdates[0] instanceof Date)) {
      data.exdates = data.exdates.map((date: string) => {
        return parseISO(date);
      });
    }

    Object.assign(this, data);

    // Ensure dtstart is always a valid Date
    if (
      !this.dtstart ||
      !(this.dtstart instanceof Date) ||
      isNaN(this.dtstart.getTime())
    ) {
      // Use the timezone from this recurrence model when defaulting dtstart
      const now = new Date();
      if (this.tzid) {
        // Convert current time to the recurrence's timezone, then get start of day
        const zonedNow = utcToZonedTime(now, this.tzid);
        this.dtstart = startOfDay(zonedNow);
      } else {
        // Fallback to local timezone if tzid is not set
        this.dtstart = startOfDay(now);
      }
    }

    const rruleSet = new RRuleSet();
    const rrule = {
      freq: this.freq,
      dtstart: this.dtstart,
      interval: this.interval,
      count: this.count,
      until: this.until ? this.until : addYears(this.dtstart, 1),
      byweekday: this.byweekday,
    };
    rruleSet.rrule(new RRule(rrule));

    if (data.exdates?.length > 0) {
      this.exdates.forEach((date) => {
        rruleSet.exdate(date);
      });
    }
    this.rruleSet = rruleSet;

    this.formattedJsDates = this.rruleSet.all().map((date) => {
      return format(startOfDay(date), 'yyyy-MM-dd');
    });
  }

  // FORMATTED RELATIVE TO TIMEZONE
  // primarily used to set value of date inputs 'yyyy MM dd'
  get stringFormattedDates() {
    // Normalize for DST: keep occurrences aligned to the site-local calendar day.
    // Compute the base offset at dtstart in the site's timezone, and adjust each occurrence
    // by the offset difference so that wall-clock midnight stays consistent across DST shifts.
    const tz = this.tzid;
    const baseOffsetMs = tz ? getTimezoneOffset(tz, this.dtstart) : 0;

    return this.all.map((date) => {
      if (tz) {
        const occurrenceOffsetMs = getTimezoneOffset(tz, date);
        const deltaHours = (baseOffsetMs - occurrenceOffsetMs) / (1000 * 60 * 60);
        const adjusted = deltaHours !== 0 ? addHours(date, deltaHours) : date;
        return formatInTimeZone(adjusted, tz, 'yyyy-MM-dd');
      }
      return format(date, 'yyyy-MM-dd');
    });
  }

  // 'all' returns dates in relative local time. If tzid was used during rule creation, this is the correct local time when it is the original time in the selected timezone
  get all() {
    return this.rruleSet.all().map((date) => {
      // RRule already returns dates in the correct timezone context, no conversion needed
      return date;
    });
  }

  get stringDtstart() {
    // RRule dtstart is already in the correct timezone context, just format it
    return format(this.rruleSet.dtstart(), 'yyyy-MM-dd');
  }

  get recipeForm() {
    let weekdays;
    if (this.byweekday?.length > 0) {
      weekdays = this.byweekday.map((weekday: Weekday | number) => {
        let switchcase = weekday;
        if (weekday.hasOwnProperty('weekday')) {
          // weekday value provided to 'day select' of form cannot have 'nth' values, still need to re-map
          switchcase = (weekday as Weekday).weekday;
        }
        let stringDay;

        switch (switchcase) {
          case RRule.MO.weekday:
            stringDay = RRule.MO;
            break;
          case RRule.TU.weekday:
            stringDay = RRule.TU;
            break;
          case RRule.WE.weekday:
            stringDay = RRule.WE;
            break;
          case RRule.TH.weekday:
            stringDay = RRule.TH;
            break;
          case RRule.FR.weekday:
            stringDay = RRule.FR;
            break;
          case RRule.SA.weekday:
            stringDay = RRule.SA;
            break;
          case RRule.SU.weekday:
            stringDay = RRule.SU;
            break;
        }
        return stringDay;
      });
    }

    return {
      freq: this.freq,
      interval: this.interval,
      count: this.count,
      until: this.until ? formatInTimeZone(this.until, this.tzid, 'yyyy-MM-dd') : null,
      nbyweekday: this.nbyweekday,
      byweekday: weekdays ? weekdays : this.byweekday,
      stopCondition: this.until ? 'date' : 'never',
    };
  }

  // properties used to create an Rrule
  get recipeRrule() {
    return {
      freq: this.freq,
      dtstart: this.dtstart,
      interval: this.interval,
      count: this.count,
      until: this.until,
      nbyweekday: this.nbyweekday,
      byweekday: this.byweekday,
      tzid: this.tzid ? this.tzid : null,
    };
  }

  // FORMATTED RELATIVE TO TIMEZONE
  get stringLastTimezoneRecurrence() {
    const lastDate =
      this.stringFormattedDates[this.stringFormattedDates.length - 1];
    const parsedDate = parse(lastDate, 'yyyy-MM-dd', new Date());
    return format(parsedDate, 'EEEE, MMM d, yyyy');
  }
}
